/**
 * <juice-popup> — the theme-native welcome popup that replaces the Mailchimp
 * (`mcforms-*`) popup. Shows once per shopper per frequency window, after a
 * delay or on exit intent; never on cart/checkout; focus-trapped; Escape closes.
 */
class JuicePopup extends HTMLElement {
  connectedCallback() {
    this.key = this.dataset.storageKey || 'juice-popup-1';
    this.days = parseInt(this.dataset.frequencyDays, 10) || 30;

    if (this.#suppressed()) return;

    this.card = this.querySelector('.jp__card');
    this.querySelectorAll('[data-jp-close]').forEach((el) =>
      el.addEventListener('click', () => this.close(true))
    );
    this._onKey = (e) => {
      if (e.key === 'Escape') this.close(true);
      if (e.key === 'Tab') this.#trap(e);
    };

    // The form posts to Klaviyo over fetch now, so there's no page reload to react to.
    // On success: stop nagging this shopper, and leave the confirmation up long enough
    // to actually be read before closing.
    this.addEventListener('juice:subscribed', () => {
      this.#remember();
      setTimeout(() => this.close(false), 2600);
    });

    const delay = (parseInt(this.dataset.delay, 10) || 0) * 1000;
    this._timer = setTimeout(() => this.open(), delay);

    if (this.dataset.exitIntent === 'true' && window.matchMedia('(min-width: 990px)').matches) {
      this._onLeave = (e) => {
        if (e.clientY <= 0) this.open();
      };
      document.addEventListener('mouseout', this._onLeave);
    }
  }

  disconnectedCallback() {
    clearTimeout(this._timer);
    document.removeEventListener('keydown', this._onKey);
    if (this._onLeave) document.removeEventListener('mouseout', this._onLeave);
  }

  #suppressed() {
    // Never interrupt the checkout funnel.
    const p = window.location.pathname;
    if (p.startsWith('/cart') || p.startsWith('/checkout') || p.includes('/challenge')) return true;
    if (window.Shopify && window.Shopify.designMode) return false; // always preview in the editor
    try {
      const until = localStorage.getItem(this.key);
      if (until && Date.now() < parseInt(until, 10)) return true;
    } catch (e) {
      /* storage blocked — just show it */
    }
    return false;
  }

  #remember() {
    try {
      localStorage.setItem(this.key, String(Date.now() + this.days * 864e5));
    } catch (e) {
      /* ignore */
    }
  }

  #focusable() {
    return Array.from(
      this.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.offsetParent !== null);
  }

  #trap(e) {
    const els = this.#focusable();
    if (!els.length) return;
    const first = els[0];
    const last = els[els.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  open() {
    if (this.opened) return;
    this.opened = true;
    clearTimeout(this._timer);
    if (this._onLeave) document.removeEventListener('mouseout', this._onLeave);
    this.hidden = false;
    this.lastFocus = document.activeElement;
    document.addEventListener('keydown', this._onKey);
    const email = this.querySelector('.jp__input') || this.querySelector('[data-jp-close]');
    if (email) setTimeout(() => email.focus({ preventScroll: true }), 80);
  }

  close(remember) {
    this.hidden = true;
    document.removeEventListener('keydown', this._onKey);
    if (remember) this.#remember();
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
  }
}

if (!customElements.get('juice-popup')) customElements.define('juice-popup', JuicePopup);
